// Pure helpers for the Store Regions page: no network, no store, so they can be
// tested on their own.

// /v1/regions may answer with plain strings or with objects; either way the
// page only needs the name it sends back to POST /v1/stores/region.
export const regionName = (item) => {
  if (typeof item === "string") return item.trim();
  return String(item?.name ?? item?.region ?? item?.regionName ?? "").trim();
};

export const CSV_TEMPLATE_HEADERS = [
  { label: "storeCode", key: "storeCode" },
  { label: "region", key: "region" },
  { label: "state", key: "state" },
  { label: "city", key: "city" },
  { label: "cityId", key: "cityId" },
  { label: "cityCode", key: "cityCode" },
];

const normalizeHeader = (value) => value.toLowerCase().replace(/[^a-z]/g, "");

const STORE_HEADERS = ["storecode", "store", "code"];
const REGION_HEADERS = ["region", "regionname"];
const STATE_HEADERS = ["state", "statename"];
const CITY_HEADERS = ["city", "cityname"];
const CITY_ID_HEADERS = ["cityid"];

const nameKey = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

// Edit distance between two strings, for "did you mean" suggestions.
const editDistance = (a, b) => {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const current = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length];
};

// The item in `items` ({ name }) whose name matches, ignoring case and spacing.
export const findByName = (items, name) => {
  const key = nameKey(name);
  return key ? items.find((item) => nameKey(item?.name) === key) ?? null : null;
};

// The closest name in `items` to a misspelled one, or null when nothing is
// close enough to be worth suggesting: at least half the letters must line up
// ("Gurgaon" -> "Gurugram"), or one name must contain the other.
export const suggestName = (items, name) => {
  const key = nameKey(name);
  if (!key) return null;
  let best = null;
  let bestScore = 0;
  for (const item of items) {
    const candidate = nameKey(item?.name);
    if (!candidate) continue;
    const contains = candidate.includes(key) || key.includes(candidate);
    const score = contains
      ? 0.9
      : 1 - editDistance(key, candidate) / Math.max(key.length, candidate.length);
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return bestScore >= 0.5 ? best.name : null;
};

const didYouMean = (items, name) => {
  const suggestion = suggestName(items, name);
  return suggestion ? ` Did you mean "${suggestion}"?` : "";
};

const findColumn = (headers, names) => headers.findIndex((h) => names.includes(h));

// The state names a parsed CSV uses, so the page can load those states'
// cities before building the plan. Empty when there is no state column.
export const csvStateNames = (rows) => {
  if (!rows?.length) return [];
  const stateCol = findColumn(rows[0].map(normalizeHeader), STATE_HEADERS);
  if (stateCol === -1) return [];
  const names = new Map();
  for (const cells of rows.slice(1)) {
    const name = (cells[stateCol] ?? "").trim();
    if (name && !names.has(nameKey(name))) names.set(nameKey(name), name);
  }
  return [...names.values()];
};

// Minimal RFC 4180 reader: quoted fields, "" escapes, commas and newlines
// inside quotes, CRLF or LF line ends. Blank lines are dropped.
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  const source = String(text ?? "").replace(/^﻿/, "");

  for (let i = 0; i < source.length; i++) {
    const char = source[i];

    if (inQuotes) {
      if (char === '"' && source[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }

  row.push(field);
  rows.push(row);

  return rows
    .map((cells) => cells.map((cell) => cell.trim()))
    .filter((cells) => cells.some(Boolean));
};

// Turns parsed CSV rows into the batches to send: one per region, for
// POST /v1/stores/region, and one per city, for POST /v1/stores/location.
//
// The first row must be a header naming a store code column; region, state
// and city columns are optional.
//
// Region: a row with no region of its own falls back to `fallbackRegion` (the
// one picked on the page), so a CSV that is just a list of store codes assigns
// them all to that region. Matched case-insensitively against `regions` and
// sent in its spelling.
//
// Location: a new state and city is given as the state's name, matched
// (ignoring case and spacing) against `states`, and the city's id, looked up
// in `citiesByState[stateId]` - by id, since city names repeat. The city name
// may be blank; if given it must be that city's. A row whose state and city
// are the store's current ones needs no id and is left alone, as is one with
// all three blank. A misspelled state gets a "did you mean".
// `locations` (store code -> { state, city, cityId? }) marks stores already in
// that city, which are not sent again.
//
// Store codes are checked against the stores this user can see when that list
// is given. Every rejected row is reported with its line number rather than
// silently skipped. A store listed twice keeps its last row.
export const buildCsvAssignments = (
  rows,
  {
    regions = [],
    storeCodes,
    fallbackRegion = "",
    states = [],
    citiesByState = {},
    locations = {},
  } = {}
) => {
  const errors = [];
  const empty = { groups: {}, locationGroups: {}, alreadySet: 0, total: 0 };

  if (!rows.length) {
    return { ...empty, errors: [{ line: 0, message: "The file is empty." }] };
  }

  const headers = rows[0].map(normalizeHeader);
  const storeCol = findColumn(headers, STORE_HEADERS);
  const regionCol = findColumn(headers, REGION_HEADERS);
  const stateCol = findColumn(headers, STATE_HEADERS);
  const cityCol = findColumn(headers, CITY_HEADERS);
  const cityIdCol = findColumn(headers, CITY_ID_HEADERS);

  if (storeCol === -1) {
    return { ...empty, errors: [{ line: 1, message: 'Header row needs a "storeCode" column.' }] };
  }

  const regionByKey = {};
  for (let region of regions) regionByKey[region.toLowerCase()] = region;

  const knownCodes = storeCodes
    ? new Set([...storeCodes].map((code) => String(code).toUpperCase()))
    : null;

  // store code -> { region?, place? }, last row wins.
  const planned = new Map();

  rows.slice(1).forEach((cells, index) => {
    const line = index + 2;
    const cell = (column) => (column === -1 ? "" : (cells[column] ?? "").trim());
    const code = cell(storeCol).toUpperCase();
    const stateName = cell(stateCol);
    const cityName = cell(cityCol);
    const cityId = cell(cityIdCol);

    if (!code) {
      errors.push({ line, message: "Missing store code." });
      return;
    }

    if (knownCodes && !knownCodes.has(code)) {
      errors.push({ line, message: `Unknown store code "${code}".` });
      return;
    }

    let place = null;
    // The store's current location written back unchanged needs no cityId:
    // nothing is sent for it.
    let kept = false;
    if (stateName || cityName || cityId) {
      const current = locations[code];
      kept =
        !cityId &&
        Boolean(current) &&
        nameKey(current.state) === nameKey(stateName) &&
        nameKey(current.city) === nameKey(cityName);
      if (!kept) {
        if (!stateName) {
          errors.push({ line, message: `City "${cityName || cityId}" needs a state.` });
          return;
        }
        if (!cityId) {
          errors.push({
            line,
            message: `${code}: a new state or city needs its cityId (see States & Cities CSV).`,
          });
          return;
        }
        const state = findByName(states, stateName);
        if (!state) {
          errors.push({ line, message: `Unknown state "${stateName}".${didYouMean(states, stateName)}` });
          return;
        }
        const cities = citiesByState[state._id];
        if (!cities) {
          errors.push({ line, message: `Could not load the cities of ${state.name}.` });
          return;
        }
        const city = cities.find((candidate) => String(candidate._id) === cityId);
        if (!city) {
          errors.push({ line, message: `City id "${cityId}" is not a city in ${state.name}.` });
          return;
        }
        if (cityName && nameKey(cityName) !== nameKey(city.name)) {
          errors.push({
            line,
            message: `City id "${cityId}" is ${city.name}, not "${cityName}". Change the cityId to match.`,
          });
          return;
        }
        place = { state, city };
      }
    }

    const rawRegion = cell(regionCol) || fallbackRegion;
    let region = null;
    if (rawRegion) {
      region = regionByKey[rawRegion.toLowerCase()];
      if (!region) {
        errors.push({ line, message: `Unknown region "${rawRegion}" for ${code}.` });
        return;
      }
    }

    if (!region && !place && !kept) {
      errors.push({
        line,
        message:
          stateCol === -1 && cityCol === -1
            ? `No region given for ${code}.`
            : `Nothing to set for ${code}: no region, state or city.`,
      });
      return;
    }

    planned.set(code, { region, place, kept });
  });

  const groups = {};
  const locationGroups = {};
  const updated = new Set();
  let alreadySet = 0;

  for (let [code, { region, place, kept }] of planned) {
    if (kept) alreadySet += 1;
    if (region) {
      if (!groups[region]) groups[region] = [];
      groups[region].push(code);
      updated.add(code);
    }
    if (place) {
      const current = locations[code];
      const sameCity = current?.cityId
        ? String(current.cityId) === String(place.city._id)
        : current &&
          nameKey(current.city) === nameKey(place.city.name) &&
          nameKey(current.state) === nameKey(place.state.name);
      if (sameCity) {
        alreadySet += 1;
        continue;
      }
      const id = place.city._id;
      if (!locationGroups[id]) {
        locationGroups[id] = {
          cityId: id,
          city: place.city.name,
          stateId: place.state._id,
          state: place.state.name,
          storeCodes: [],
        };
      }
      locationGroups[id].storeCodes.push(code);
      updated.add(code);
    }
  }

  return { groups, locationGroups, errors, alreadySet, total: updated.size };
};

// The store's current region, if GET /v1/stores carries one.
export const storeRegion = (store) => regionName(store?.region ?? store?.regionName ?? "");

// Template rows: every store passed in with its current region and location,
// so the downloaded file is the current state ready to edit and upload back.
// Uses storeState / storeCity, defined below.
export const templateRows = (stores) =>
  stores.map((store) => ({
    storeCode: store.code,
    region: storeRegion(store),
    state: storeState(store),
    city: storeCity(store),
    cityId: storeCityId(store),
    cityCode: storeCityCode(store),
  }));

// The full download: the upload columns plus the store's name, which an
// upload ignores but makes the file easier to read.
export const exportRows = (stores) =>
  stores.map((store) => ({
    storeCode: store.code,
    storeName: store.houseName || store.name || "",
    region: storeRegion(store),
    state: storeState(store),
    city: storeCity(store),
    cityId: storeCityId(store),
    cityCode: storeCityCode(store),
  }));

// Fills in the cityId and cityCode of rows that name a state and city but
// lack them, where the state's cities are loaded: by the row's cityId if it
// has one, else the one city with that name. Rows it cannot settle keep them
// blank.
export const fillCityIds = (rows, states, citiesByState) =>
  rows.map((row) => {
    if ((row.cityId && row.cityCode) || !row.state || !row.city) return row;
    const state = findByName(states, row.state);
    const matches = (state && citiesByState[state._id]) || [];
    const named = row.cityId
      ? matches.filter((city) => String(city._id) === String(row.cityId))
      : matches.filter((city) => nameKey(city.name) === nameKey(row.city));
    if (named.length !== 1) return row;
    return { ...row, cityId: String(named[0]._id), cityCode: row.cityCode || cityCode(named[0]) };
  });

// The state names rows (from templateRows, exportRows, sampleRows) use.
export const rowStateNames = (rows) => {
  const names = new Map();
  for (const row of rows) {
    if (row.state && !names.has(nameKey(row.state))) names.set(nameKey(row.state), row.state);
  }
  return [...names.values()];
};

// Every state with its cities, one row per city, for looking up the ids.
// `citiesByState` maps a state's _id to its cities; a state with none still
// gets a row, with the city columns empty.
export const locationRows = (states, citiesByState) =>
  states.flatMap((state) => {
    const cities = citiesByState[state._id] ?? [];
    if (cities.length === 0) {
      return [{ stateName: state.name, stateId: state._id, cityName: "", cityId: "", cityCode: "" }];
    }
    return cities.map((city) => ({
      stateName: state.name,
      stateId: state._id,
      cityName: city.name,
      cityId: city._id,
      cityCode: cityCode(city),
    }));
  });

// A short example file showing the format. Uses real store codes, region names
// and a real state and city when they are loaded, so the sample uploads
// cleanly as-is. `place` is { state, city, cityId?, cityCode? }; without one the sample
// falls back to a placeholder. The rows show each case: region and
// location, region only, and (last) no region, which uses the selected one.
export const sampleRows = (stores = [], regions = [], place = null) => {
  const codes = stores.map((store) => store.code).filter(Boolean);
  const pickCode = (i) => codes[i] ?? `DPI045${6 + i}`;
  const pickRegion = (i) => regions[i % Math.max(regions.length, 1)] ?? (i % 2 ? "DPI West" : "DPI East");
  const { state = "Punjab", city = "Abohar", cityId = "", cityCode = "" } = place ?? {};

  return [
    { storeCode: pickCode(0), region: pickRegion(0), state, city, cityId, cityCode },
    { storeCode: pickCode(1), region: pickRegion(0), state: "", city: "", cityId: "", cityCode: "" },
    { storeCode: pickCode(2), region: pickRegion(1), state, city, cityId, cityCode },
    { storeCode: pickCode(3), region: "", state, city, cityId, cityCode },
  ];
};

// A real { state, city, cityId, cityCode } for the sample: the first store that has one.
export const samplePlace = (stores = []) => {
  const store = stores.find((candidate) => storeState(candidate) && storeCity(candidate));
  return store
    ? {
        state: storeState(store),
        city: storeCity(store),
        cityId: storeCityId(store),
        cityCode: storeCityCode(store),
      }
    : null;
};

// A location field as a plain name. The service may send a name or an object
// ({ _id, name }); either reads the same.
export const locationName = (value) => {
  if (typeof value === "string") return value.trim();
  return String(value?.name ?? value?.cityName ?? value?.stateName ?? "").trim();
};

// The store's current state and city, if GET /v1/stores carries them - at the
// top level or under `location`.
export const storeState = (store) =>
  locationName(store?.state ?? store?.stateName ?? store?.location?.state ?? store?.location?.stateName);

export const storeCity = (store) =>
  locationName(store?.city ?? store?.cityName ?? store?.location?.city ?? store?.location?.cityName);

// The store's current city id, when GET /v1/stores carries one: as cityId, or
// as the _id of a city object, at the top level or under `location`.
export const storeCityId = (store) =>
  String(
    store?.cityId ??
      store?.city?._id ??
      store?.location?.cityId ??
      store?.location?.city?._id ??
      ""
  ).trim();

// A city's code, as cityCode or code. Blank when the service sends none.
export const cityCode = (city) => String(city?.cityCode ?? city?.code ?? "").trim();

// The store's current city code, when GET /v1/stores carries one: as
// cityCode, or on a city object, at the top level or under `location`.
export const storeCityCode = (store) =>
  String(
    store?.cityCode ??
      store?.location?.cityCode ??
      (cityCode(store?.city) || cityCode(store?.location?.city))
  ).trim();

export const sortByName = (items) =>
  [...items].sort((a, b) => String(a?.name ?? "").localeCompare(String(b?.name ?? "")));

//------------------------------------ Editing in the table ---------------------------------

// The table's unsaved edits are keyed by store code:
//   { region?: name, location?: { stateId, state, cityId, city } }
// A location with no cityId is a state picked but no city yet: shown, but not
// saved until a city is chosen (the service sets the state from the city).

const sameName = (a, b) => nameKey(a) === nameKey(b);

// One row per store, with the edits laid over what the service reported.
export const storeMasterRows = (stores, edits = {}) =>
  stores.map((store) => {
    const code = String(store.code);
    const edit = edits[code] || {};
    const savedRegion = storeRegion(store);
    const savedState = storeState(store);
    const savedCity = storeCity(store);
    const location = edit.location;
    return {
      code,
      name: store.houseName || store.name || code,
      savedRegion,
      savedState,
      savedCity,
      region: edit.region ?? savedRegion,
      state: location ? location.state : savedState,
      city: location ? location.city : savedCity,
      stateId: location?.stateId ?? null,
      cityId: location?.cityId ?? null,
      locationIncomplete: Boolean(location && !location.cityId),
      changed: Object.keys(edit).length > 0,
      store,
    };
  });

const withField = (edits, code, field, value) => {
  const { [field]: dropped, ...rest } = edits[code] || {};
  const next = value === undefined ? rest : { ...rest, [field]: value };
  const { [code]: old, ...others } = edits;
  return Object.keys(next).length ? { ...others, [code]: next } : others;
};

// Sets a row's region; back to the saved one drops the edit.
export const setRegionEdit = (edits, row, region) =>
  withField(edits, row.code, "region", sameName(region, row.savedRegion) ? undefined : region);

// Picks a row's state. Its city has to be picked again, unless this is the
// saved state, which puts the saved location back.
export const setStateEdit = (edits, row, state) =>
  withField(
    edits,
    row.code,
    "location",
    sameName(state.name, row.savedState)
      ? undefined
      : { stateId: state._id, state: state.name, cityId: null, city: "" }
  );

// Picks a row's city in `state`; the saved location drops the edit.
export const setCityEdit = (edits, row, state, city) =>
  withField(
    edits,
    row.code,
    "location",
    sameName(state.name, row.savedState) && sameName(city.name, row.savedCity)
      ? undefined
      : { stateId: state._id, state: state.name, cityId: city._id, city: city.name }
  );

// What Save sends: one region batch per region, one location batch per city.
// Rows with a state but no city yet are listed apart and not sent.
export const storeSaveRequests = (edits = {}) => {
  const regionGroups = {};
  const locationGroups = {};
  const incomplete = [];
  for (const [code, edit] of Object.entries(edits)) {
    if (edit.region) (regionGroups[edit.region] = regionGroups[edit.region] || []).push(code);
    if (edit.location) {
      const { stateId, state, cityId, city } = edit.location;
      if (!cityId) {
        incomplete.push(code);
        continue;
      }
      if (!locationGroups[cityId]) locationGroups[cityId] = { cityId, city, stateId, state, storeCodes: [] };
      locationGroups[cityId].storeCodes.push(code);
    }
  }
  return { regionGroups, locationGroups, incomplete };
};

// The edits left once `field` saved for `codes`.
export const clearStoreSaved = (edits, field, codes) =>
  codes.reduce((next, code) => withField(next, code, field, undefined), edits);

// A CSV plan applied as table edits, for review before Save.
export const applyStorePlan = (edits, plan) => {
  let next = edits;
  for (const [region, codes] of Object.entries(plan.groups ?? {})) {
    for (const code of codes) next = withField(next, code, "region", region);
  }
  for (const group of Object.values(plan.locationGroups ?? {})) {
    const location = { stateId: group.stateId, state: group.state, cityId: group.cityId, city: group.city };
    for (const code of group.storeCodes) next = withField(next, code, "location", location);
  }
  return next;
};

export const matchesStoreSearch = (row, search) => {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return [row.name, row.code, row.region, row.state, row.city]
    .some((value) => String(value ?? "").toLowerCase().includes(term));
};

// The fields a store is set up with: blank ones mark it "Not set".
export const STORE_SETUP_FIELDS = ["region", "state", "city"];
